/**
 * One urgency scale for the whole product.
 *
 * The backend speaks five dialects for the same concept, which is why the old
 * UI rendered triage, health-check and alert severity with unrelated colours:
 *
 *   health check / alerts  green | yellow | orange | red   (URGENCY_ORDER)
 *   triage records         HIJAU | KUNING | MERAH          (3 levels, uppercase)
 *   AI assessment          monitor | urgent | emergency
 *   MRI Assist             routine_review | urgent_review | repeat_images | emergency
 *   conversation priority  Normal | Kritis
 *
 * Everything is normalised here once, so a red is a red everywhere. Nothing
 * downstream is allowed to invent its own mapping.
 *
 * `tests/test_urgency_contract.py` asserts that every value the backend can
 * emit appears below — an unmapped level silently becomes "monitor", which
 * both understates an emergency and overstates a normal result.
 */

export const URGENCY_LEVELS = ["routine", "monitor", "urgent", "critical"] as const;
export type Urgency = (typeof URGENCY_LEVELS)[number];

const ALIASES: Record<string, Urgency> = {
  // health check + alert severity
  green: "routine",
  yellow: "monitor",
  orange: "urgent",
  red: "critical",
  // triage records
  hijau: "routine",
  kuning: "monitor",
  merah: "critical",
  // AI assessment vocabulary
  routine: "routine",
  monitor: "monitor",
  urgent: "urgent",
  emergency: "critical",
  critical: "critical",
  // MRI Assist. `repeat_images` is a picture-quality verdict rather than a
  // clinical one, but it still needs action from the patient, so it lands on
  // monitor rather than routine.
  routine_review: "routine",
  urgent_review: "urgent",
  repeat_images: "monitor",
  // Conversation priority. A separate, operational vocabulary — mapped here so
  // the colours stay consistent, but rendered with its own wording by
  // <PriorityBadge>: a chat queue item is not a clinical finding.
  kritis: "critical",
  tinggi: "urgent",
  sedang: "monitor",
  normal: "routine",
  rendah: "routine",
};

/**
 * Unknown values deliberately fall back to `monitor`, never `routine` —
 * a value we failed to parse must not be presented to a patient as "fine".
 */
export function normalizeUrgency(value: string | null | undefined): Urgency {
  if (!value) return "monitor";
  return ALIASES[value.trim().toLowerCase()] ?? "monitor";
}

export function urgencyRank(level: Urgency) {
  return URGENCY_LEVELS.indexOf(level);
}

export function isAtLeast(level: Urgency, threshold: Urgency) {
  return urgencyRank(level) >= urgencyRank(threshold);
}

type UrgencyMeta = {
  label: string;
  /** Shown under the label on result screens so the level means something. */
  guidance: string;
  dot: string;
  badge: string;
  panel: string;
  bar: string;
};

export const URGENCY_META: Record<Urgency, UrgencyMeta> = {
  routine: {
    label: "Aman",
    guidance: "Tidak ditemukan tanda yang memerlukan tindakan segera.",
    dot: "bg-routine-500",
    badge: "bg-routine-tint text-routine-text ring-1 ring-routine-500/25",
    panel: "bg-routine-tint border-routine-500/30",
    bar: "bg-routine-500",
  },
  monitor: {
    label: "Perlu dipantau",
    guidance: "Pantau gejala dan ulangi pemeriksaan dalam beberapa hari.",
    dot: "bg-monitor-500",
    badge: "bg-monitor-tint text-monitor-text ring-1 ring-monitor-500/25",
    panel: "bg-monitor-tint border-monitor-500/30",
    bar: "bg-monitor-500",
  },
  urgent: {
    label: "Perlu diperiksa",
    guidance: "Konsultasikan ke dokter dalam waktu dekat.",
    dot: "bg-urgent-500",
    badge: "bg-urgent-tint text-urgent-text ring-1 ring-urgent-500/25",
    panel: "bg-urgent-tint border-urgent-500/30",
    bar: "bg-urgent-500",
  },
  critical: {
    label: "Darurat",
    guidance: "Segera hubungi 119 atau kunjungi IGD terdekat.",
    dot: "bg-critical-500",
    badge: "bg-critical-tint text-critical-text ring-1 ring-critical-500/25",
    panel: "bg-critical-tint border-critical-500/40",
    bar: "bg-critical-500",
  },
};
